import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRight } from 'assets/icons/chevron.svg';
import Button from 'components/Button/Button';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Panel from 'components/Panel/Panel';
import StepItem from 'components/StepItem/StepItem';
import TitleTableWrapper from 'components/TitleTableWrapper/TitleTableWrapper';
import Tooltip from 'components/Tooltip/Tooltip';
import { TASK_HEAD_GEO_ANALYZE_CELLS, TASK_HEAD_GEOGRAPHIC_ANALYZE_CELLS, TASK_HEAD_GEOGRAPHIC_CELLS } from 'constants/dataSets';
import { BUTTON_VARIANT, DELETE_MODAL_TITLES } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import CreateGeo from './components/CreateCompetitor/CreateGeo';
import ItemsTable from './components/ItemsTable/ItemsTable';
import TableAnalyze from './components/TableAnalyze/TableAnalyze';

import type IMaterialityConfigGeographic from './interfaces/IMaterialityConfigGeographic';

import styles from './scss/MaterialityConfigGeographic.module.scss';

function MaterialityConfigGeographicView({
  isEmptyList, loading, isLoadingMore, onOpenModal, goBack, saveCompetitor, openModal,
  items, hasMore, onLoadMore, valueToggle, onChangeToggle,
  onDeleteItem, isDeleteModalOpen, onCancel, onDeleteConfirm, defaultItems,
  onUpdate,
  recordId, vcmId, setOffset, setOpenModal, dateUpdated,
  onSortChange, orderBy, locationsCountries, refreshAffectedToken,
  isViewOnly = false,
  isAffectedDataOutdated,
  isLastSearchOutdated,
  isRefreshProcessing,
  disableNext,
  disableSave,
}: IMaterialityConfigGeographic) {
  const { t } = useTranslation();

  const isRefreshDataAIEnabled = isAffectedDataOutdated;
  const isRefreshDataEnabled = isLastSearchOutdated;
  const isPageBusy = Boolean(loading || isRefreshProcessing);
  const processingTooltip = t('materialityAssessment.geographicRefreshProcessingTooltip');
  const saveBlockedTooltip = isRefreshProcessing
    ? processingTooltip
    : t('materialityAssessment.geographicRefreshSaveBlocked');
  const nextBlockedTooltip = disableNext ? processingTooltip : '';

  const isUpdateDisabled = Boolean(
    loading
    || isRefreshProcessing
    || isViewOnly
    || (valueToggle ? !isRefreshDataEnabled : !isRefreshDataAIEnabled),
  );
  let tooltipTitleUpdate = '';

  if (isRefreshProcessing) {
    tooltipTitleUpdate = processingTooltip;
  } else if (!isUpdateDisabled) {
    tooltipTitleUpdate = t('materialityAssessment.refreshDaGeo');
  }

  return (
    <Panel className={styles.root}>
      <Button
        title={t('materialityAssessment.back')}
        onClick={goBack}
        variant={BUTTON_VARIANT.ghost}
        startIcon={<ArrowLeftIcon />}
      />
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          <TitleTableWrapper
            title={t('materialityAssessment.geographic_specific')}
            hint={t('materialityAssessment.sensitive_regions')}
            buttonTitle={t('materialityAssessment.addGeographicSpecific')}
            onButtonClick={onOpenModal}
            buttonClassName={styles.button}
            disableButton={valueToggle || isRefreshProcessing || isViewOnly}
            onUpdate={onUpdate}
            disableIconButton={isUpdateDisabled}
            tooltipTitleUpdate={tooltipTitleUpdate}
            titleUpdateBtn={valueToggle ? t('common.refreshData') : t('common.refreshDataAI')}
            dateUpdated={dateUpdated}
            showButton
          />
          <div className={styles.steps}>
            <StepItem title={t('materialityAssessment.step1')} step={1} done={!valueToggle} />
            <ArrowRight className={styles.arrowIcon} />
            <StepItem title={t('materialityAssessment.step2')} step={2} done={valueToggle} />
          </div>
          <div className={styles.contentWrapper}>
            {isPageBusy ? (
              <ContentLoader
                title={isRefreshProcessing
                  ? t('materialityAssessment.geographicRefreshProcessing')
                  : t('materialityAssessment.loadingGeo')}
              />
            ) : null}
            {isEmptyList && !isPageBusy ? (
              <EmptyTaskManagement
                title={t('materialityAssessment.emptyGeographicDescription')}
                checkIcon
              />
            ) : null}
            {!isEmptyList && !isPageBusy ? (
              <div className={styles.tableWrapper}>
                {defaultItems && defaultItems.length > 0 && !valueToggle ? (
                  <ItemsTable
                    items={defaultItems || []}
                    headCells={TASK_HEAD_GEOGRAPHIC_CELLS}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore || false}
                    onDeleteItem={onDeleteItem}
                    onSortChange={onSortChange}
                    orderBy={orderBy}
                    locationsCountries={locationsCountries}
                    isInfoTable
                  />
                ) : null}
                {valueToggle ? (
                  <TableAnalyze
                    items={items || []}
                    headCells={TASK_HEAD_GEOGRAPHIC_ANALYZE_CELLS}
                    headCellsMiniTable={TASK_HEAD_GEO_ANALYZE_CELLS}
                    maId={recordId}
                    vcmId={vcmId}
                    onSortChange={onSortChange}
                    locationsCountries={locationsCountries}
                    orderBy={orderBy}
                    refreshAffectedToken={refreshAffectedToken}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={goBack}
          />
          <div className={styles.rightButtons}>
            {valueToggle ? (
              <Button
                title={t('common.prev')}
                onClick={() => onChangeToggle(false)}
                variant={BUTTON_VARIANT.cancel}
                className={styles.prevButton}
              />
            ) : null}
            {valueToggle ? (
              <Tooltip hint={disableSave ? saveBlockedTooltip : ''}>
                <Button
                  title={t('common.save')}
                  onClick={saveCompetitor}
                  className={styles.button}
                  disabled={disableSave}
                />
              </Tooltip>
            ) : (
              <Tooltip hint={nextBlockedTooltip}>
                <Button
                  title={t('common.next')}
                  onClick={() => onChangeToggle(true)}
                  className={styles.button}
                  disabled={disableNext}
                />
              </Tooltip>
            )}
          </div>
        </div>
        <CreateGeo
          open={openModal}
          onClose={onOpenModal}
          title={t('materialityAssessment.addGeographicSpecific')}
          recordId={recordId}
          setOffset={setOffset}
          setOpenModal={setOpenModal}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          onReject={onCancel}
          isLoading={false}
          title={DELETE_MODAL_TITLES.DELETE_SECTOR_SPECIFIC}
          hint={DELETE_MODAL_TITLES.DELETE_SECTOR_SPECIFIC_HINT}
          onConfirm={onDeleteConfirm}
        />
      </div>
    </Panel>
  );
}

export default MaterialityConfigGeographicView;
